<template>
  <BGModal :shown="dialogShown"
           :title="$t('admin_survey.detail_title')"
           fixed-tabs>
    <template #content>
      <v-container class="edit-survey-content" fluid v-if="survey">
        <v-tabs fixed-tabs
                v-model="tabIndex">
          <v-tab>{{ $t('register.basic_info') }}</v-tab>
          <v-tab-item>
            <v-form v-model="isFormValid"
                    ref="addSurveyForm">
              <v-text-field readonly
                            :label="$t('admin_survey.survey_link')"
                            :value="surveyLink">
                <template #append-outer>
                  <v-tooltip top>
                    <template #activator="{on, attrs}">
                      <v-btn icon
                             v-on="on"
                             v-bind="attrs"
                             v-clipboard:copy="surveyLink">
                        <v-icon>fas fa-copy</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('copy') }}</span>
                  </v-tooltip>
                </template>
              </v-text-field>
              <v-text-field outlined
                            :label="$t('admin_survey.title_en')"
                            v-model="survey.title_en"
                            :rules="requiredRules"></v-text-field>
              <v-text-field outlined
                            :label="$t('admin_survey.title_zh')"
                            v-model="survey.title_zh"
                            :rules="requiredRules"></v-text-field>
              <v-input :rules="requiredRules"
                       v-model="survey.start">
                <v-container fluid class="flex-column pa-0">
                  <legend class="v-label theme--light">{{ $t('admin_survey.start') }}</legend>
                  <DateTimePickerInput v-model="survey.start"
                                       hide-details></DateTimePickerInput>
                </v-container>
              </v-input>
              <v-input :rules="surveyEndRules"
                       v-model="survey.end">
                <v-container fluid class="flex-column pa-0">
                  <legend class="v-label theme--light">{{ $t('admin_survey.end') }}</legend>
                  <DateTimePickerInput v-model="survey.end"
                                       hide-details></DateTimePickerInput>
                </v-container>
              </v-input>
              <v-checkbox :label="$t('admin_survey.require_login')"
                          v-model="survey.require_login"></v-checkbox>
              <v-row class="pt-0">
                <v-col>
                  <v-input hide-details
                           v-model="survey.description_en">
                    <v-container fluid class="flex-column">
                      <legend class="v-label theme--light">{{ $t('admin_survey.description_en') }}</legend>
                      <TinyMceEditor v-model="survey.description_en"></TinyMceEditor>
                    </v-container>
                  </v-input>
                </v-col>
              </v-row>
              <v-row class="pt-0">
                <v-col>
                  <v-input hide-details
                           v-model="survey.description_zh">
                    <v-container fluid class="flex-column">
                      <legend class="v-label theme--light">{{ $t('admin_survey.description_zh') }}</legend>
                      <TinyMceEditor v-model="survey.description_zh"></TinyMceEditor>
                    </v-container>
                  </v-input>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-input hide-details>
                    <v-container fluid class="flex-column pa-0">
                      <legend class="v-label theme--light">{{ $t('admin_event_survey.questions') }}</legend>

                      <draggable :list="questions"
                                 draggable=".question"
                                 handle=".question-handle"
                                 @change="onQuestionMoved"
                                 class="v-expansion-panels v-item-group theme--light draggable-list flex-1">
                        <div class="question v-expansion-panel flex-column"
                             v-for="(question, idx) in questions"
                             :key="question.id">
                          <v-row class="v-expansion-panel-header">
                            <v-btn icon
                                   class="question-handle">
                              <v-icon class="question-handle">fas fa-grip-lines</v-icon>
                            </v-btn>
                            <v-col class="question-title"
                                   @click="editingQuestionIndex = idx">
                              <v-row v-if="idx !== editingQuestionIndex">
                                <v-col>
                                  {{ question.title_en }}
                                </v-col>
                              </v-row>
                              <v-row v-if="idx !== editingQuestionIndex">
                                <v-col>
                                  {{ question.title_zh }}
                                </v-col>
                              </v-row>
                            </v-col>
                            <v-btn icon
                                   class="question-expand"
                                   v-if="idx !== editingQuestionIndex"
                                   @click="editingQuestionIndex = idx"
                                   key="open">
                              <v-icon>fas fa-sort-down</v-icon>
                            </v-btn>
                            <v-btn icon
                                   class="question-expand"
                                   v-if="idx === editingQuestionIndex"
                                   @click="editingQuestionIndex = -1"
                                   key="close">
                              <v-icon>fas fa-sort-up</v-icon>
                            </v-btn>
                          </v-row>
                          <v-container v-show="idx === editingQuestionIndex">
                            <v-row>
                              <v-col>
                                <v-text-field readonly
                                              :label="$t('admin_event_survey.question_type')"
                                              :value="$t('admin_event_survey.' + question.type)"></v-text-field>
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col>
                                <v-text-field outlined
                                              :label="$t('admin_event_survey.question_title_en')"
                                              v-model="question.title_en"></v-text-field>
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col>
                                <v-text-field outlined
                                              :label="$t('admin_event_survey.question_title_zh')"
                                              v-model="question.title_zh"></v-text-field>
                              </v-col>
                            </v-row>
                            <v-row v-if="question.type === 'rating'">
                              <v-col>
                                <v-text-field outlined
                                              :label="$t('admin_event_survey.question_min_label_en')"
                                              v-model="question.min_label_en"></v-text-field>
                              </v-col>
                              <v-col>
                                <v-text-field outlined
                                              :label="$t('admin_event_survey.question_max_label_en')"
                                              v-model="question.max_label_en"></v-text-field>
                              </v-col>
                              <v-col>
                                <v-text-field outlined
                                              :label="$t('admin_event_survey.question_min_label_zh')"
                                              v-model="question.min_label_zh"></v-text-field>
                              </v-col>
                              <v-col>
                                <v-text-field outlined
                                              :label="$t('admin_event_survey.question_max_label_zh')"
                                              v-model="question.max_label_zh"></v-text-field>
                              </v-col>
                            </v-row>
                            <v-row>
                              <v-col>
                                <v-checkbox :label="$t('admin_event_survey.question_required')"
                                            v-model="question.required"></v-checkbox>
                              </v-col>
                            </v-row>
                            <v-divider></v-divider>
                            <v-row>
                              <v-col class="text-right">
                                <v-tooltip top>
                                  <template #activator="{on, attrs}">
                                    <v-btn icon
                                           v-on="on"
                                           v-bind="attrs"
                                           @click="cloneQuestion(idx)">
                                      <v-icon>fas fa-clone</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>{{ $t('admin_event_survey.clone_question') }}</span>
                                </v-tooltip>
                                <v-tooltip top>
                                  <template #activator="{on, attrs}">
                                    <v-btn icon
                                           v-on="on"
                                           v-bind="attrs"
                                           @click="deleteQuestion(idx)">
                                      <v-icon>fas fa-trash</v-icon>
                                    </v-btn>
                                  </template>
                                  <span>{{ $t('admin_event_survey.delete_question') }}</span>
                                </v-tooltip>
                              </v-col>
                            </v-row>
                          </v-container>
                        </div>
                      </draggable>
                    </v-container>
                  </v-input>
                </v-col>
              </v-row>
              <v-row>
                <!--        <v-col>-->
                <!--          <v-btn block-->
                <!--                 @click="addQuestion('single_line')">{{ $t('admin_survey.add_single_line_question') }}-->
                <!--          </v-btn>-->
                <!--        </v-col>-->
                <v-col>
                  <v-btn block
                         @click="addQuestion('freetext')">{{ $t('admin_event_survey.add_freetext_question') }}
                  </v-btn>
                </v-col>
                <v-col>
                  <v-btn block
                         @click="addQuestion('rating')">{{ $t('admin_event_survey.add_rating_question') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-tab-item>

          <v-tab>{{ $t('admin_hkiarace.email_template') }}</v-tab>
          <v-tab-item>
            <SurveyEmailTemplate :survey-id="surveyId"
                                 :changed.sync="emailTemplateChanged"
                                 :save.sync="saveEmailTemplate"
                                 :send.sync="publishSurvey"
                                 v-if="tabIndex === 1"></SurveyEmailTemplate>
          </v-tab-item>
        </v-tabs>
      </v-container>
    </template>

    <template #footer>
      <v-row no-gutters>
        <v-btn @click="cancel"
               class="mr-3"
               depressed
               large>
          {{ $t('cancel') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary"
               depressed
               large
               v-if="tabIndex === 0"
               @click="$refs.surveyForm.validate() && save()">
          {{ $t('save') }}
          <v-icon right>fas fa-check</v-icon>
        </v-btn>
        <v-btn color="primary"
               class="ml-3"
               depressed
               large
               v-if="tabIndex === 1"
               :disabled="!emailTemplateChanged"
               @click="saveEmailTemplate"
               key="save">
          {{ $t('save') }}
          <v-icon right>fas fa-check</v-icon>
        </v-btn>
        <v-btn class="ml-3"
               depressed
               large
               v-if="tabIndex === 1"
               @click="publishSurvey"
               key="publish">
          {{ $t('admin_survey.publish_survey') }}
          <v-icon right>fas fa-paper-plane</v-icon>
        </v-btn>
      </v-row>
    </template>
  </BGModal>
</template>
<script>
import draggable from 'vuedraggable';
import BGModal from "@/components/BGModal";
import {computed, ref, watch} from "@vue/composition-api";
import DateTimePickerInput from "@/components/DateTimePickerInput";
import TinyMceEditor from "@/components/TinyMceEditor";
import _ from "lodash";
import uuid4 from "uuid/v4";
import SurveyEmailTemplate from "@/components/survey/SurveyEmailTemplate";

export default {
  name: 'EditSurveyModal',
  components: {SurveyEmailTemplate, TinyMceEditor, DateTimePickerInput, BGModal, draggable},
  props: {
    shown: Boolean,
    surveyId: String,
  },
  setup(props, {root, emit}) {
    const dialogShown = ref(props.shown);

    const tabIndex = ref(0);

    const requiredRules = ref([
      value => !!value || root.$t('register.required_fields'),
    ]);

    // Init
    watch(() => props.shown, async (newValue, oldValue) => {
      if (newValue !== oldValue) {
        dialogShown.value = newValue;

        if (newValue) {
          tabIndex.value = 0;
          getSurvey();
        }
      }

      tabIndex.value = 0;
    });
    const cancel = function () {
      emit('cancel');
    };

    const isFormValid = ref(false);
    const survey = ref(null);
    const getSurvey = function () {
      root.$callAPI('event/admin/survey/', {survey_id: props.surveyId}, (response) => {
        survey.value = response.body.survey;

        questions.value = response.body.survey ? _.sortBy(response.body.survey.questions, 'order') : [];
      })
    };
    const surveyEndRules = ref([
      ...requiredRules.value,
      value => (!survey.value.start || value > survey.value.start) || 'Please enter valid survey start / end',
    ])
    const surveyLink = computed(() => {
      if (!survey.value) return;

      return window.location.protocol + "//" + window.location.hostname + "/" +
          root.$router.resolve({name: 'survey-start', params: {surveyId: props.surveyId}}).href;
    })

    const questions = ref([]);
    const editingQuestionIndex = ref(0);
    const addQuestion = function (type) {
      questions.value.push({
        id: uuid4(),
        title_en: 'Question',
        title_zh: '問題',
        type: type,
        required: false,
        min_label_en: 'Min',
        max_label_en: 'Max',
        min_label_zh: '最小',
        max_label_zh: '最大',
      });
    }
    const onQuestionMoved = function (e) {
      if (e.moved) {
        editingQuestionIndex.value = e.moved.newIndex;
      }
    }
    const cloneQuestion = function (idx) {
      questions.value.splice(idx + 1, 0,
          {
            ...questions.value[idx],
            id: uuid4(),
          }
      )
      editingQuestionIndex.value = idx + 1;
    }
    const deleteQuestion = function (idx) {
      questions.value.splice(idx, 1);
      editingQuestionIndex.value = -1;
    }

    const save = function () {
      let params = {
        ...survey.value,
        survey_id: props.surveyId,
        questions: questions.value.map(function (q, index) {
          return {
            ...q,
            order: index,
          }
        }),
      }

      root.$callAPI('event/survey/edit/', params, (response) => {
        emit('finish', response.body.survey);
      });
    };

    // Email Template
    const emailTemplateChanged = ref(false);
    const saveEmailTemplate = null;
    const publishSurvey = null;


    return {
      dialogShown,
      tabIndex,
      cancel,

      isFormValid,
      requiredRules,

      survey,
      surveyEndRules,
      surveyLink,

      questions,
      editingQuestionIndex,
      addQuestion,
      onQuestionMoved,
      cloneQuestion,
      deleteQuestion,

      save,

      emailTemplateChanged,
      saveEmailTemplate,
      publishSurvey,
    }
  },
}
</script>

<style lang="less">
.edit-survey-content {
  display: flex;
  height: 100%;

  .question {
    .question-handle, .question-expand {
      flex: 0 0 auto;
    }

    .question-title {
      flex: 1 0 0 !important;
      padding-top: 16px;
    }
  }
}
</style>
